import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    DoCheck,
    HostListener,
    OnInit,
    Signal,
    effect,
    inject,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { B2E, MOBILIAR, OivCoreSharedFacade, TealiumTrackingFacade } from '@mobi/oiv-viewer-utils-ng-jslib';
import { UserSessionService } from '@mobi/rwc-utils-ng-jslib';

import { INDEX_SEARCH, NAVIGATION } from '../../../util/constant/common';
import { isHTMLElementInViewport } from '../../../util/dom';
import { CoverSheet } from '../../navigation/model/cover-sheet';
import { OivViewerFacade } from '../data/facade/oiv-viewer-facade';
import { EidNavigationService } from '../data/service/eid-navigation.service';

@Component({
    selector: 'oiv-viewer-main-layout',
    templateUrl: './main-layout.component.html',
    styleUrl: './main-layout.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class MainLayoutComponent implements OnInit, DoCheck {
    #oivViewerFacade = inject(OivViewerFacade);
    #coreSharedFacade = inject(OivCoreSharedFacade);
    #tealiumTrackingFacade = inject(TealiumTrackingFacade);
    #userSessionService = inject(UserSessionService);
    #route = inject(ActivatedRoute);
    #router = inject(Router);
    #title = inject(Title);
    #eidNavigationService = inject(EidNavigationService);
    #destroyRef = inject(DestroyRef);

    protected readonly INDEX_SEARCH = INDEX_SEARCH;
    protected readonly NAVIGATION = NAVIGATION;
    readonly WAITING_TIME = 7000;

    appSource = this.#coreSharedFacade.appSource;
    avbChapters = this.#oivViewerFacade.avbChapters;
    appContext = this.#coreSharedFacade.appContext;
    coverSheet: Signal<CoverSheet> = this.#oivViewerFacade.coverSheet;
    isMobile = this.#coreSharedFacade.isMobile;

    isOpenOverlay = this.#coreSharedFacade.isOverlayOpen;
    majorValue = signal<string>('');
    showScrollButton = signal(false);

    sideBarStyleObject: any;
    scrollingUp = false;
    lastScrollPosition = 0;
    tabs = [NAVIGATION, INDEX_SEARCH];

    #timeoutId: any;

    #appTitle = toSignal(this.#coreSharedFacade.translatedMessage('oiv.app.title'), {
        initialValue: 'Allgemeine Bedingungen',
    });
    #appMobiliar = toSignal(this.#coreSharedFacade.translatedMessage('oiv.app.mobiliar'), {
        initialValue: 'die Mobiliar',
    });
    #appProtekta = toSignal(this.#coreSharedFacade.translatedMessage('oiv.app.protekta'), {
        initialValue: 'Protekta',
    });
    #translatedSwitchMenuTitle = toSignal(this.#coreSharedFacade.translatedMessage('oiv.navigation.splitButton.menu'), {
        initialValue: 'Inhalt',
    });
    #translatedSwitchSearchTitle = toSignal(
        this.#coreSharedFacade.translatedMessage('oiv.navigation.splitButton.search'),
        { initialValue: 'Suche' },
    );

    constructor() {
        effect(() => {
            if (this.coverSheet().title && this.coverSheet().info) {
                this.#updateAppTitle();
            }
        });
    }

    ngOnInit() {
        this.#coreSharedFacade.approvalMode = this.#route.snapshot.data['approvalMode'] || false;
        this.#oivViewerFacade.load(this.#route.snapshot.params['urlName'] || '');
        this.#eidNavigationService.handleEidQueryParam(this.#destroyRef);
        this.#route.queryParamMap.subscribe(paramMap => {
            const tabValue = paramMap.get('tab');
            if (this.tabs.some(item => item === tabValue)) {
                this.handleMajorValueChange(paramMap.get('tab'));
            } else {
                this.handleMajorValueChange(NAVIGATION);
            }
        });
        this.#setupTealiumTracking();
        this.checkScrollPosition();
    }

    ngDoCheck(): void {
        this.updateStickySidebarWidth();
        this.addSidebarPaddingToPreventFooterOverlap();
    }

    @HostListener('window:resize')
    onResize() {
        this.updateStickySidebarWidth();
        this.addSidebarPaddingToPreventFooterOverlap();
    }

    @HostListener('window:scroll', [])
    checkScrollPosition() {
        const scrollPosition = document.documentElement.scrollTop;
        // Check if User scrolls up
        this.scrollingUp = scrollPosition < this.lastScrollPosition;

        this.lastScrollPosition = scrollPosition;

        if (scrollPosition > window.innerHeight && this.scrollingUp) {
            this.showScrollButton.set(true);
            // Wait 7 seconds before hiding the button
            clearTimeout(this.#timeoutId); // Kill the previous timeout section every time user's scroll
            this.#timeoutId = setTimeout(() => {
                this.showScrollButton.set(false);
            }, this.WAITING_TIME);
        } else {
            this.showScrollButton.set(false);
        }
    }

    updateStickySidebarWidth() {
        this.sideBarStyleObject = {
            ...this.sideBarStyleObject,
            ...{ width: document.getElementById('sticky-sidebar-wrapper')?.offsetWidth + 'px' },
        };
    }

    addSidebarPaddingToPreventFooterOverlap() {
        const paddingFooter = isHTMLElementInViewport(document.getElementById('oiv-footer-wrapper')) ? '100px' : '0px';
        this.sideBarStyleObject = { ...this.sideBarStyleObject, ...{ paddingBottom: paddingFooter } };
    }

    handleMajorValueChange(value: any) {
        this.majorValue.set(value as string);
        this.#router.navigate([], {
            relativeTo: this.#route,
            queryParams: { tab: this.majorValue() }, // add or update other params as needed
            queryParamsHandling: 'merge', // merge with existing params, or use '' to replace all
            replaceUrl: true, // replace the current history entry
            fragment: this.#router.url.split('#')[1], // preserve fragment if needed
        });
        // Track the switcher event
        this.#trackSwitcherEvent(value);
    }

    scrollToTop() {
        window.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
    }

    #setupTealiumTracking() {
        this.#tealiumTrackingFacade.addVolatile('user_loginStatus', this.appContext() === B2E ? 'true' : 'false');
        this.#tealiumTrackingFacade.addVolatile('rwc_user_session_id', this.#userSessionService.getId());
    }

    #trackSwitcherEvent(value: string) {
        const switcherValue = () => {
            switch (value) {
                case NAVIGATION:
                    return this.#translatedSwitchMenuTitle();
                case INDEX_SEARCH:
                    return this.#translatedSwitchSearchTitle();
                default:
                    return '';
            }
        };
        this.#tealiumTrackingFacade.trackInteractionEvent('search-qa', 'switcher', switcherValue(), 'switch');
    }

    #updateAppTitle() {
        const edition = this.coverSheet().info.split(' ');
        const editionDate = edition.slice(1).join(' '); // Remove the first word from the edition
        const avbTitleAndEdition = `${this.coverSheet().title} ${editionDate}`;
        const appTitle = this.#coreSharedFacade.appSource() === MOBILIAR ? this.#appMobiliar() : this.#appProtekta();
        this.#title.setTitle(`${this.#appTitle()} ${avbTitleAndEdition} | ${appTitle}`);
    }
}
